import { InvoicePosition } from '@shared/models/invoice-position';
import { getTaxRateValue } from '@core/functions/get-tax-rate-value';
import { StaticGlobals } from '@modules/bcm/@shared/static-globals';
import { roundNumber, RoundNumberFactor } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';

export function getPriceDataFromInvoicePositions(positions: InvoicePosition[], signMultiplier: number = 1) {
    const totalTaxes: { [key: number]: number } = {};
    const totalNetByTaxRate: { [key: number]: number } = {};
    let totalNetPrice = 0;
    let totalGrossPrice = 0;
    let totalTaxFreeProductsPrice = 0;

    let currentTotalPrice: number;
    let currentTotalGrossPrice: number;
    // Ungerundeter Netto-Wert je Position – wird erst nach der Summierung gerundet,
    // um Akkumulierung von Rundungsfehlern über mehrere Positionen zu vermeiden.
    let currentTotalNetPriceRaw: number;

    positions.forEach(position => {

        const {quantity, totalPrice} = position;
        const taxRateValue = getTaxRateValue(position);

        if (totalPrice == null || quantity == null) {
            return;
        }

        currentTotalPrice = totalPrice * signMultiplier;

        if (StaticGlobals.netPrices) {
            // Netto-Eingabe: Netto runden, Brutto daraus ableiten
            const roundFactor = StaticGlobals.roundTo5Cent
                ? RoundNumberFactor.Swiss5CentimesRounding
                : RoundNumberFactor.TwoDecimals;
            currentTotalNetPriceRaw = roundNumber(currentTotalPrice, roundFactor);
            const grossPriceRaw = currentTotalNetPriceRaw * (1 + (taxRateValue / 100));
            currentTotalGrossPrice = roundNumber(grossPriceRaw, roundFactor);
        } else {
            // Brutto-Eingabe: Brutto runden, Netto als exakten Rohwert ableiten (NICHT runden)
            const roundFactor = StaticGlobals.roundTo5Cent
                ? RoundNumberFactor.Swiss5CentimesRounding
                : RoundNumberFactor.TwoDecimals;
            currentTotalGrossPrice = roundNumber(currentTotalPrice, roundFactor);
            currentTotalNetPriceRaw = currentTotalGrossPrice / (1 + (taxRateValue / 100));
        }

        // Steuer aus ungerundetem Netto ableiten – verhindert Cent-Differenzen
        const currentTotalTaxRaw = currentTotalGrossPrice - currentTotalNetPriceRaw;

        totalTaxes[taxRateValue] = totalTaxes[taxRateValue] || 0;
        totalTaxes[taxRateValue] += currentTotalTaxRaw;

        totalNetByTaxRate[taxRateValue] = totalNetByTaxRate[taxRateValue] || 0;
        totalNetByTaxRate[taxRateValue] += currentTotalNetPriceRaw;

        totalGrossPrice += currentTotalGrossPrice;
        totalNetPrice += currentTotalNetPriceRaw;

        if (taxRateValue === 0) {
            totalTaxFreeProductsPrice += currentTotalNetPriceRaw;
        }
    });

    // Erst nach vollständiger Summierung auf 2 Dezimalen runden
    Object
        .keys(totalTaxes)
        .forEach((key) => totalTaxes[key] = roundNumber(totalTaxes[key], RoundNumberFactor.TwoDecimals));

    Object
        .keys(totalNetByTaxRate)
        .forEach((key) => totalNetByTaxRate[key] = roundNumber(totalNetByTaxRate[key], RoundNumberFactor.TwoDecimals));

    // totalTax aus den bereits gerundeten Einzelsteuern ableiten – konsistent mit totalTaxes-Einträgen
    const totalTaxRounded = roundNumber(
        Object.values(totalTaxes).reduce((sum, v) => sum + v, 0),
        RoundNumberFactor.TwoDecimals
    );

    return {
        totalNetPrice: roundNumber(totalNetPrice, RoundNumberFactor.TwoDecimals),
        totalGrossPrice: roundNumber(totalGrossPrice, RoundNumberFactor.TwoDecimals),
        totalTaxFreeProductsPrice: roundNumber(totalTaxFreeProductsPrice, RoundNumberFactor.TwoDecimals),
        totalTax: totalTaxRounded,
        totalTaxes,
        totalNetByTaxRate,
    };
}
